<template>
  <div ref="wrapperRef" class="m-html-wrapper">
    <div v-if="displayMode" class="m-html-display" :class="htmlClasses">
      <h3 v-if="htmlTitle" class="m-html-title">{{ htmlTitle }}</h3>
      <iframe
        v-if="useWidgetIframe"
        ref="widgetIframeRef"
        class="m-html-widget-frame"
        :class="{ 'm-html-widget-frame--overlay': widgetOverlay }"
        sandbox="allow-scripts"
        :srcdoc="widgetSrcdoc"
        :style="widgetFrameStyle"
        title="HTML preview"
      />
      <div v-else class="m-html-markup" :class="fieldStyleScopeClass">
        <component v-if="fieldStylesheet" :is="'style'">{{ fieldStylesheet }}</component>
        <div v-html="sanitizedHtml" @click="handleLinkClick" @submit.capture="handleFormSubmit"></div>
      </div>
    </div>
    <Textarea v-else v-model="htmlContent" :rows="rows" :autoResize="true" :disabled="disabled" :readonly="readonly"
      :placeholder="placeholder" class="m-html-editor" @blur="handleBlur" />

    <div v-if="hint" class="m-html-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Textarea from 'primevue/textarea'
import { rewriteHtmlImageSrc } from '@/common/utils/rewriteHtmlImageSrc'
import { wrapHtmlInnerWithRootAttributes } from '@/common/utils/mhtmlRootWrapper'
import { sanitizePlatformFieldHtml } from '@/common/utils/html-sanitizer'
import { splitMetadataStyleWrapperCss } from '@/common/utils/elementStyleWrapper'
import { scopeCssToAncestor } from '@/common/utils/mhtmlFieldStyles'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import {
  buildMhtmlWidgetSrcdoc,
  htmlFragmentContainsScript,
  isMhtmlPreviewNavigableHref,
  applyMhtmlWidgetResize,
  parseMhtmlWidgetResizeMessage,
  unescapeHtmlEntitiesIfNeeded,
} from '@/common/utils/mhtmlWidgetPreview'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { SkipNextPageLeaveConfirmKey } from '@/components/platform/injection-keys'
import { isCrossOriginFormAction } from '@/common/utils/pageReloadGuard'
import { buildPlainUrlAnchorHtml, looksLikePlainUrl } from '@/common/utils/mhtmlPlainUrl'

interface Props {
  modelValue?: string
  editable?: boolean
  /** Platform HTML fields: render markup instead of empty editor when value is not yet bound. */
  preferDisplayMode?: boolean
  rows?: number
  placeholder?: string
  hint?: string
  disabled?: boolean
  readonly?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  editable: false,
  rows: 10,
  placeholder: '',
  hint: '',
  disabled: false,
  readonly: false,
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
  'link-click': [url: string]
  'blur': []
}>()

const htmlContent = ref(props.modelValue)
const wrapperRef = ref<HTMLElement | null>(null)
const skipNextPageLeaveConfirm = inject(SkipNextPageLeaveConfirmKey, undefined)
const widgetIframeRef = ref<HTMLIFrameElement | null>(null)
const widgetFrameId = `mhtml-w-${Math.random().toString(36).slice(2, 11)}`
const fieldStyleScopeClass = `mhtml-scope-${widgetFrameId.replace(/[^a-zA-Z0-9_-]/g, '')}`
const widgetHeight = ref(32)
const widgetOverlay = ref(false)

const looksLikeHtml = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const s = value.trim()
  if (!s) return false
  return /<\/?[a-z][\s\S]*>/i.test(s) || s.includes('&lt;') || s.includes('&gt;')
}

// Extract title and body content from full HTML documents
const extractHtmlContent = (html: string): { title: string; body: string } => {
  if (!html || typeof html !== 'string') {
    return { title: '', body: html }
  }

  const trimmed = html.trim()
  const lowerTrimmed = trimmed.toLowerCase()

  if (lowerTrimmed.startsWith('<html')) {
    let title = ''
    let body = ''

    const titleMatch = trimmed.match(/<title[^>]*>(.*?)<\/title>/is)
    if (titleMatch && titleMatch[1]) {
      title = titleMatch[1].trim()
    }

    const bodyMatch = trimmed.match(/<body[^>]*>\s*(.*?)\s*<\/body>/is)
    if (bodyMatch && bodyMatch[1]) {
      body = bodyMatch[1].trim()
    } else {
      const htmlOpenMatch = trimmed.match(/<html\b([^>]*)>/is)
      const htmlMatch = trimmed.match(/<html[^>]*>\s*(.*?)\s*<\/html>/is)
      if (htmlMatch && htmlMatch[1]) {
        let content = htmlMatch[1].trim()
        content = content.replace(/<head[^>]*>.*?<\/head>/is, '')
        const rootTag = htmlOpenMatch ? `<html${htmlOpenMatch[1]}>` : '<html>'
        body = wrapHtmlInnerWithRootAttributes(content.trim(), rootTag)
      } else {
        body = trimmed
      }
    }

    return { title, body }
  }

  return { title: '', body: trimmed }
}


const htmlContentExtracted = computed(() => {
  if (!props.modelValue) return { title: '', body: '' }

  const htmlString = typeof props.modelValue === 'string'
    ? props.modelValue
    : String(props.modelValue)

  if (!htmlString) return { title: '', body: '' }

  return extractHtmlContent(unescapeHtmlEntitiesIfNeeded(htmlString))
})

// Get HTML title
const htmlTitle = computed(() => {
  return htmlContentExtracted.value.title
})

// Sanitize HTML body content
const sanitizedHtml = computed(() => {
  const bodyContent = useWidgetIframe.value
    ? htmlContentExtracted.value.body
    : splitMetadataStyleWrapperCss(htmlContentExtracted.value.body).markupHtml
  if (!bodyContent) {
    const raw =
      typeof props.modelValue === 'string'
        ? props.modelValue.trim()
        : String(props.modelValue ?? '').trim()
    if (looksLikePlainUrl(raw)) {
      return buildPlainUrlAnchorHtml(raw)
    }
    return ''
  }

  if (looksLikePlainUrl(bodyContent)) {
    return buildPlainUrlAnchorHtml(bodyContent)
  }

  return rewriteHtmlImageSrc(sanitizePlatformFieldHtml(bodyContent))
})

const useWidgetIframe = computed(() => {
  const body = htmlContentExtracted.value.body
  return Boolean(body) && htmlFragmentContainsScript(body)
})

const fieldStylesheet = computed(() => {
  if (useWidgetIframe.value) return ''
  const { cssText } = splitMetadataStyleWrapperCss(htmlContentExtracted.value.body)
  return scopeCssToAncestor(cssText, `.${fieldStyleScopeClass}`)
})

const widgetSrcdoc = computed(() => {
  if (!useWidgetIframe.value) return ''
  return buildMhtmlWidgetSrcdoc(htmlContentExtracted.value.body, widgetFrameId)
})

const widgetFrameStyle = computed(() => {
  if (widgetOverlay.value) {
    return { height: '100vh' }
  }
  return { height: `${Math.max(Math.ceil(widgetHeight.value), 32)}px` }
})

const htmlClasses = computed(() => [
  'm-html',
  {
    'm-html--disabled': props.disabled
  }
])

const displayMode = computed(() => {
  if (!props.editable) return true
  if (props.preferDisplayMode) return true
  return looksLikeHtml(props.modelValue) || looksLikePlainUrl(props.modelValue)
})

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

// Handle hyperlink clicks
const handleLinkClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement

  const anchor = target.closest('a')
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!isMhtmlPreviewNavigableHref(href)) return

  event.preventDefault()
  emit('link-click', href!)
  window.open(href!, '_blank')
}

const handleWidgetResizeMessage = (event: MessageEvent) => {
  const parsed = parseMhtmlWidgetResizeMessage(event.data)
  if (!parsed || parsed.id !== widgetFrameId) return
  const frame = widgetIframeRef.value
  if (frame && event.source && event.source !== frame.contentWindow) return
  const next = applyMhtmlWidgetResize(
    { height: widgetHeight.value, overlay: widgetOverlay.value },
    parsed,
  )
  widgetHeight.value = next.height
  widgetOverlay.value = next.overlay
}

const handleFormSubmit = (event: Event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return

  const action = form.getAttribute('action') ?? ''
  if (!isCrossOriginFormAction(action, window.location.origin)) return

  skipNextPageLeaveConfirm?.()
}

// Watch for changes in editor mode
watch(htmlContent, (newValue) => {
  emit('update:modelValue', newValue)
  emit('change', newValue)
})

// Sync with external modelValue changes
watch(() => props.modelValue, (newValue) => {
  htmlContent.value = newValue
})

watch(useWidgetIframe, (enabled) => {
  if (!enabled) {
    widgetOverlay.value = false
    widgetHeight.value = 32
  }
})

onMounted(() => {
  window.addEventListener('message', handleWidgetResizeMessage)
})

onUnmounted(() => {
  window.removeEventListener('message', handleWidgetResizeMessage)
})

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-html-wrapper {
  width: 100%;
}

.m-html-display {
  width: 100%;
  padding: 0.75rem;
  background: #ffffff !important;
  color: #000000;
  min-height: 0;
  overflow-y: visible;

  .m-html-title {
    margin: 0 0 0.75rem 0;
    padding: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.5rem;
  }

  :deep(p) {
    margin: 0;
    padding: 0;
    line-height: 1.5em;
    color: inherit;
  }

  :deep(div) {
    color: inherit;
  }

  :deep(span) {
    color: inherit;
  }

  :deep(table) {
    margin-top: 0;
    padding: 0;
    border-collapse: collapse;
  }

  :deep(h3) {
    margin-bottom: 0;
  }

  :deep(a) {
    color: var(--primary-color, #1e3a6e);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: color-mix(in srgb, var(--primary-color, #1e3a6e) 85%, black);
    }
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 1.5rem;
  }

  :deep(code) {
    background: #f3f4f6;
    padding: 0.125rem 0.25rem;
    border-radius: 2px;
    font-family: monospace;
  }

  :deep(pre) {
    background: #f3f4f6;
    padding: 0.75rem;
    border-radius: 4px;
    overflow-x: auto;
  }

  :deep(form) {
    display: block;
    margin: 0.5rem 0;
  }

  :deep(input[type='submit']),
  :deep(button[type='submit']) {
    display: inline-block;
    margin-top: 0.75rem;
    padding: 0.5rem 1.25rem;
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    background: var(--primary-color, #1e3a6e);
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  :deep(input[type='submit']:hover),
  :deep(button[type='submit']:hover) {
    background: color-mix(in srgb, var(--primary-color, #1e3a6e) 85%, black);
  }

  &--disabled {
    opacity: 0.6;
    background: #f9fafb;
  }
}

.m-html-widget-frame {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
}

.m-html-widget-frame--overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 50000;
}

.m-html-editor {
  width: 100%;
  font-family: monospace;
  font-size: 0.875rem;
}

.m-html-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>
